import React from 'react'
import { createPortal } from 'react-dom'
import { UploadAttachmentsButton } from 'components/attachments/upload-attachments'
import { AttachmentDisplay } from 'components/attachments/display-attachment'
import { uploadAttachments } from 'components/attachments/upload-attachment-vm'
import {
  validateFiles,
  formatValidationErrors,
} from 'src/utils/file-validation'
import { successToast } from 'src/utils/success-toast'
import { errorToast } from 'src/utils/error-toast'

type AttachmentState = {
  id: string
  filename: string
  uploaded_by_user_name?: string
  uploaded_by_user_id?: string | number
  url?: string
  uploading?: boolean
  error?: string
}

type CheckinUploadAttachmentsProps = {
  skillId: number | string
  hiddenFieldName?: string
  currentUserId?: string | number
  current_user_id?: string | number
  disabled?: boolean
  existingAttachments?: AttachmentState[]
  onAttachmentsChange?: (attachments: AttachmentState[]) => void
  stage?: string
}

export const CheckinUploadAttachments: React.FC<
  CheckinUploadAttachmentsProps
> = ({
  currentUserId,
  current_user_id,
  skillId,
  hiddenFieldName = `checkin[checkin_skills_attributes][skill_${skillId}][attachments][]`,
  disabled = false,
  existingAttachments = [],
  onAttachmentsChange,
  stage,
}) => {
  const userId = currentUserId || current_user_id
  const [attachments, setAttachments] =
    React.useState<AttachmentState[]>(existingAttachments)
  const prevExistingAttachmentsRef =
    React.useRef<AttachmentState[]>(existingAttachments)

  React.useEffect(() => {
    const prevIds = prevExistingAttachmentsRef.current
      .map((att) => att.id)
      .sort()
      .join(',')
    const currentIds = existingAttachments
      .map((att) => att.id)
      .sort()
      .join(',')

    if (prevIds !== currentIds) {
      setAttachments(existingAttachments)
      prevExistingAttachmentsRef.current = existingAttachments
    }
  }, [existingAttachments])
  const [isUploading, setIsUploading] = React.useState(false)

  const updateHiddenFormFields = React.useCallback(() => {
    const container = document.getElementById(`skill_${skillId}`)
    const form = container?.closest('form') as HTMLFormElement | null

    if (!form) {
      console.warn(`Form not found for skill ${skillId}`)
      return
    }

    const existingInputs = form.querySelectorAll(
      `input[name="${hiddenFieldName}"]`
    )
    existingInputs.forEach((input) => input.remove())

    const validAttachments = attachments.filter(
      (att) =>
        att.id &&
        !att.uploading &&
        !att.error &&
        !att.id.startsWith('uploading-')
    )

    const uniqueAttachments = validAttachments.filter(
      (att, index, array) => array.findIndex((a) => a.id === att.id) === index
    )

    uniqueAttachments.forEach((attachment) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = hiddenFieldName
      input.value = attachment.id
      input.setAttribute('data-attachment-id', attachment.id)
      form.appendChild(input)
    })
  }, [attachments, hiddenFieldName, skillId])

  React.useEffect(() => {
    onAttachmentsChange?.(attachments)
  }, [attachments, onAttachmentsChange])

  React.useEffect(() => {
    updateHiddenFormFields()
  }, [attachments, updateHiddenFormFields])

  const handleFileSelect = async (files: FileList) => {
    const filesArray = Array.from(files)
    setIsUploading(true)

    const currentStageCount = attachments.length

    const validation = validateFiles(filesArray, {
      stage,
      currentStageCounts: stage ? { [stage]: currentStageCount } : {},
    })

    if (validation.errors.length > 0) {
      const errorMessage = formatValidationErrors(validation.errors)
      errorToast(errorMessage)
      setIsUploading(false)
      return
    }

    const validFiles = validation.validFiles

    const uploadingAttachments: AttachmentState[] = validFiles.map(
      (file, index) => ({
        id: `uploading-${Date.now()}-${index}`,
        filename: file.name,
        uploading: true,
      })
    )

    setAttachments((prev) => [...prev, ...uploadingAttachments])

    try {
      const signedIds = await uploadAttachments(validFiles, stage)
      setAttachments((prev) =>
        prev.map((attachment) => {
          const uploadIndex = uploadingAttachments.findIndex(
            (ua) => ua.id === attachment.id
          )
          if (
            uploadIndex !== -1 &&
            uploadIndex < signedIds.length &&
            signedIds[uploadIndex]
          ) {
            return {
              ...attachment,
              id: signedIds[uploadIndex],
              uploading: false,
              error: undefined,
            }
          }
          return attachment
        })
      )

      const fileCount = validFiles.length
      const message =
        fileCount === 1
          ? 'File uploaded successfully'
          : `${fileCount} files uploaded successfully`
      successToast(message)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed'
      errorToast(errorMessage)

      setAttachments((prev) =>
        prev.map((attachment) => {
          if (uploadingAttachments.some((ua) => ua.id === attachment.id)) {
            return {
              ...attachment,
              uploading: false,
              error: errorMessage,
            }
          }
          return attachment
        })
      )
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveAttachment = async (id: string) => {
    console.log('Removing attachment with ID:', id)

    // If it's an existing attachment (not uploading), call the backend to remove it
    if (!id.startsWith('uploading-') && !id.startsWith('temp-')) {
      try {
        const checkinId = getCheckinIdFromPage()
        console.log('Checkin ID:', checkinId)

        if (!checkinId) {
          throw new Error('Could not determine checkin ID')
        }

        const formData = new FormData()
        formData.append('signed_id', id)

        const response = await fetch(
          `/checkins/${checkinId}/remove_attachment`,
          {
            method: 'DELETE',
            headers: {
              'X-CSRF-Token':
                document
                  .querySelector('meta[name="csrf-token"]')
                  ?.getAttribute('content') || '',
            },
            body: formData,
          }
        )

        if (!response.ok) {
          throw new Error(
            `Failed to remove attachment: ${response.status} ${response.statusText}`
          )
        }
        successToast('Attachment removed successfully')
      } catch (error) {
        errorToast(`Failed to remove attachment: Try again later`)
        return
      }
    }

    // Remove from local state
    setAttachments((prev) => prev.filter((att) => att.id !== id))
    setTimeout(() => {
      updateHiddenFormFields()
    }, 0)
  }

  const getCheckinIdFromPage = () => {
    // Try to get checkin ID from various sources
    const urlMatch = window.location.pathname.match(/\/checkins\/([^/]+)/)
    if (urlMatch) {
      return urlMatch[1]
    }

    // Try to get from form data
    const form = document.querySelector('form[action*="/checkins/"]')
    if (form) {
      const actionMatch = form
        .getAttribute('action')
        ?.match(/\/checkins\/([^/]+)/)
      if (actionMatch) {
        return actionMatch[1]
      }
    }

    // Try to get from data attributes
    const checkinElement = document.querySelector('[data-checkin-id]')
    if (checkinElement) {
      return checkinElement.getAttribute('data-checkin-id')
    }

    return null
  }

  const validAttachments = attachments.filter((att) => !att.error)
  const buttonContainer = document.getElementById(`upload-button-${skillId}`)
  const attachmentsContainer = document.getElementById(
    `attachments-display-${skillId}`
  )

  return (
    <>
      {buttonContainer &&
        createPortal(
          <div className="flex flex-col gap-2">
            <UploadAttachmentsButton
              onFileSelect={handleFileSelect}
              disabled={disabled}
              isUploading={isUploading}
            />
          </div>,
          buttonContainer
        )}
      {attachmentsContainer &&
        createPortal(
          <AttachmentDisplay
            attachments={validAttachments}
            showRemove={!disabled}
            onRemove={handleRemoveAttachment}
            currentUserId={userId}
            existingAttachmentIds={existingAttachments.map((att) => att.id)}
          />,
          attachmentsContainer
        )}
    </>
  )
}
